<template>
  <b-modal
    id="modal-upload-bill"
    ok-title="Submit"
    cancel-variant="outline-secondary"
    body-class="common_modal modal_visible"
    hide-footer
    style="max-height: 90vh;"
    centered
    size="lg"
  >
    <!-- hide-header
    hide-header-close -->
    <template #modal-header="{ close }">
      <h3 class="m-0">
        {{ newDocumentData.id > 0 ? "修改" : "上傳" }}帳單資料
      </h3>
      <a @click="close()">
        <font-awesome-icon style="font-size: 20px" icon="fas fa-times" />
      </a>
    </template>
    <b-overlay
      :show="loading"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      rounded="sm"
    >
      <b-form class="mt-1" @submit.prevent="onSubmit">
        <!-- 供應商選擇 (僅在供應商帳單列表頁面顯示) -->
        <div v-if="showSupplierSelect" class="mb-3">
          <b-form-group
            label="選擇供應商："
            label-for="supplier_select"
            label-cols-md="3"
            class="required"
          >
            <v-select
              id="supplier_select"
              v-model="selectedSupplier"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="computedSupplierOptions"
              :reduce="(option) => option.id"
              :get-option-label="(option) => option.name_cn || option.name || option.company_name_cn"
              placeholder="請選擇供應商"
              :clearable="false"
            >
              <template #option="{ name, name_cn, company_name_cn, is_active }">
                <div :class="{ 'text-muted': is_active === 0 }">
                  <strong>{{ name }}</strong>
                  <!-- <span v-if="is_active === 0" class="badge badge-secondary ml-2 small">未啟用</span> -->
                  <div class="text-muted small">{{ name_cn }}</div>
                  <div class="text-muted small">{{ company_name_cn }}</div>
                </div>
              </template>
            </v-select>
          </b-form-group>
        </div>

        <!-- 帳單編號 -->
        <b-row class="mb-3">
          <b-col md="6">
            <validation-provider
              #default="validationContext"
              name="帳單編號"
              rules="required"
            >
              <b-form-group
                label="帳單編號："
                label-for="bill_number"
                label-cols-md="3"
                class="required"
              >
                <b-form-input
                  id="bill_number"
                  v-model="newDocumentData.bill_number"
                  placeholder="輸入帳單編號"
                  :state="getValidationState(validationContext)"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <!-- 帳單日期 -->
          <b-col md="6">
            <b-form-group
              label="帳單日期："
              label-for="bill_date"
              label-cols-md="3"
              class="required"
            >
              <flat-pickr
                id="bill_date"
                v-model="newDocumentData.bill_date"
                :config="datePickerConfig"
                class="form-control"
                placeholder="選擇帳單日期"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- 帳單總額 -->
        <b-row class="mb-3">
          <b-col md="6">
            <validation-provider
              #default="validationContext"
              name="帳單總額"
              rules="required|min_value:0"
            >
              <b-form-group
                label="帳單總額："
                label-for="bill_total_amount"
                label-cols-md="3"
                class="required"
              >
                <b-form-input
                  id="bill_total_amount"
                  v-model="newDocumentData.bill_total_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="輸入帳單總額"
                  :state="getValidationState(validationContext)"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <div>
          <b-row
            v-for="(document, index) in newDocumentData.items"
            :key="`items_${index}`"
            class="px-1"
          >
            <b-col
              cols="12"
              class="d-flex backgroundColor5 mb-1 radius-round border-1"
            >
              <b-row class="flex-grow-1 p-2">

                <!-- 發票欄位（僅當成本開支時顯示） -->
                <b-col cols="12" md="6" v-if="newDocumentData.is_daily_expense != 1">
                  <validation-provider
                    #default="validationContext"
                    name="發票"
                    :rules="newDocumentData.is_daily_expense == 1 ? '' : 'required'"
                  >
                    <b-form-group
                      label="發票："
                      label-for="invoice_id"
                      label-cols-md="3"
                      :class="newDocumentData.is_daily_expense == 1 ? '' : 'required'"
                    >
                      <v-select
                        id="invoice_id"
                        v-model="document.invoice_id"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="invoiceOptions"
                        label="invoice_number"
                        :reduce="(option) => option.id"
                        placeholder="選擇發票"
                        :filter="invoiceSearch"
                        @input="onInvoiceSelect($event, index)"
                      >
                        <template
                                #option="{
                                  invoice_number,
                                  name_cn,
                                  address
                                }"
                              >
                                [{{ invoice_number }}] {{ name_cn || '未指定客戶' }} - {{ address || '未指定地址' }}
                              </template>
                              <template
                                #selected-option="{
                                  invoice_number,
                                  name_cn,
                                  address
                                }"
                              >
                                [{{ invoice_number }}] {{ name_cn || '未指定客戶' }} - {{ address || '未指定地址' }}
                        </template>
                      </v-select>

                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <!-- 日常開支日期欄位（僅當日常開支時顯示） -->
                <b-col cols="12" md="6" v-if="newDocumentData.is_daily_expense == 1">
                  <validation-provider
                    #default="validationContext"
                    name="開支日期"
                    :rules="newDocumentData.is_daily_expense == 1 ? 'required' : ''"
                  >
                    <b-form-group
                      label="開支日期："
                      label-for="daily_expense_date"
                      label-cols-md="3"
                      :class="newDocumentData.is_daily_expense == 1 ? 'required' : ''"
                    >
                      <flat-pickr
                        id="daily_expense_date"
                        v-model="document.daily_expense_date"
                        :config="datePickerConfig"
                        class="form-control"
                        placeholder="選擇日常開支日期"
                        :state="getValidationState(validationContext)"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                 <b-col cols="12" md="6">
                  <validation-provider
                    #default="validationContext"
                    name="帳單備註"
                  >
                    <b-form-group
                      label="帳單備註："
                      label-for="bill_remark"
                      label-cols-md="3"
                    >
                      <b-form-input
                        id="bill_remark"
                        v-model="document.title"
                        placeholder="輸入帳單備註（可選）"
                        :state="getValidationState(validationContext)"
                      />

                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="12" md="6">
                  <validation-provider
                    #default="validationContext"
                    name="金額"
                    rules="required"
                  >
                    <b-form-group
                      label="金額："
                      label-for="total_amount"
                      label-cols-md="3"
                      class="required"
                    >
                      <b-form-input
                        id="total_amount"
                        type="number"
                        step="any"
                        v-model="document.amount"
                        placeholder="輸入金額"
                        :state="getValidationState(validationContext)"
                      />

                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </b-row>
              <div
                class="d-flex flex-column justify-content-between py-50 px-25"
              >
                <feather-icon
                  size="16"
                  icon="PlusIcon"
                  class="cursor-pointer text-success"
                  @click="addItem(index)"
                />
                <feather-icon
                  size="16"
                  icon="XIcon"
                  v-if="newDocumentData.items.length > 1"
                  class="cursor-pointer text-danger"
                  @click="removeItem(index, document.id)"
                />
              </div>
            </b-col>  
          </b-row>
          <div class="mb-2 d-flex justify-content-end align-items-center">
            <div class="d-flex flex-column align-items-end">
              <span :class="totalComparisonClass">
                ${{calTotal}} / ${{newDocumentData.bill_total_amount || 0}}
              </span>
              <small v-if="showAmountWarning" class="text-danger mt-1">
                計算總額超過帳單總額，請檢查明細項目
              </small>
              <!-- 付款憑證同步警告 -->
              <small v-if="showPaymentProofWarning" class="text-warning mt-1">
                <feather-icon icon="AlertTriangleIcon" size="14" class="mr-1" />
                此帳單已有付款憑證，修改金額將同步更新付款憑證金額
              </small>
            </div>
          </div>
              
            <b-col cols="12">
                  <upload-file-single
                    v-on:file-uploaded="onFileUploaded"
                    file_delete_url="/suppliers/documents"
                    ref="childRef1"
                    :props_previewImage="documentData"
                    title="帳單"
                  ></upload-file-single>
                </b-col>

                <!-- 日常開支選項 -->
                <b-col cols="12" class="mt-3">
                  <b-form-group>
                    <b-form-checkbox
                      v-model="newDocumentData.is_daily_expense"
                      name="is_daily_expense"
                      value="1"
                      unchecked-value="0"
                    >
                      日常開支
                    </b-form-checkbox>
                    <small class="text-muted">勾選此項目表示這是日常營運薪金支出</small>
                  </b-form-group>
                </b-col>
        </div>
        <div class="d-flex mt-2 justify-content-end">
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            type="button"
            variant="outline-danger"
            class="mr-1"
            @click="hide"
          >
            取消
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            type="submit"
            :disabled="isSubmitDisabled"
          >
            <feather-icon size="16" class="mr-1" icon="CheckCircleIcon" />
            上傳
          </b-button>
        </div>
      </b-form>
    </b-overlay>
  </b-modal>
</template>
  
  <script>
import {
  BButton,
  BModal,
  VBModal,
  BCardText,
  BOverlay,
  BForm,
  BFormGroup,
  BFormInput,
  BFormFile,
  BFormCheckbox,
  BRow,
  BCol,
  BCard,
  BCardBody,
  BFormInvalidFeedback,
} from "bootstrap-vue";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import flatPickr from "vue-flatpickr-component";
import { required } from "@validations";
import { ref, watch } from "@vue/composition-api";
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import zh_CN from "vee-validate/dist/locale/zh_CN.json";
localize("zh_cn", zh_CN);
import formValidation from "@core/comp-functions/forms/form-validation";
import uploadFileSingle from "@/layouts/components/file/uploadFileSingle.vue";
import axiosIns from "@/libs/axios.js";

export default {
  components: {
    BButton,
    BModal,
    VBModal,
    BCardText,
    BOverlay,
    BForm,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormCheckbox,
    BRow,
    BCol,
    BCardBody,
    BCard,
    BFormInvalidFeedback,
    vSelect,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
    uploadFileSingle,
  },
  directives: {
    "b-modal": VBModal,
    Ripple,
  },
  props: {
    documentData: {
      type: Object,
      required: true,
    },
    apiUrl: {
      type: String,
      required: true,
    },
    user_id: {
      type: Number,
      required: true,
    },
    invoiceOptions: {
      type: Array,
      required: true,
    },
    showSupplierSelect: {
      type: Boolean,
      default: false,
    },
    supplierOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      required,
      localSupplierOptions: [],
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        allowInput: true,
        locale: 'zh',
      },
    };
  },
  computed: {
    calTotal() {
      return this.newDocumentData.items.reduce((total, item) => total + Number(item.amount), 0);
    },
    computedSupplierOptions() {
      // 優先使用父組件傳遞的 supplierOptions，否則使用本地獲取的
      return this.supplierOptions && this.supplierOptions.length > 0
        ? this.supplierOptions
        : this.localSupplierOptions;
    },
    showAmountWarning() {
      const billTotal = Number(this.newDocumentData.bill_total_amount) || 0;
      return billTotal > 0 && this.calTotal > billTotal;
    },
    totalComparisonClass() {
      const billTotal = Number(this.newDocumentData.bill_total_amount) || 0;
      if (billTotal > 0 && this.calTotal > billTotal) {
        return 'text-danger font-weight-bold';
      }
      return 'text-muted';
    },
    isSubmitDisabled() {
      const billTotal = Number(this.newDocumentData.bill_total_amount) || 0;
      return billTotal > 0 && this.calTotal > billTotal;
    },
    showPaymentProofWarning() {
      // 檢查是否為編輯模式且已有付款憑證
      const isEdit = this.newDocumentData.id > 0;
      const hasPaymentProof = this.documentData.payment_proof_path || this.documentData.payment_proof_url;

      // 檢查金額是否有變更
      const originalAmount = Number(this.documentData.bill_total_amount) || 0;
      const currentAmount = Number(this.newDocumentData.bill_total_amount) || 0;
      const amountChanged = originalAmount !== currentAmount;

      return isEdit && hasPaymentProof && amountChanged;
    }
  },
  methods: {
    hide() {
      this.$bvModal.hide("modal-upload-bill");
      // 重置選中的供應商
      this.selectedSupplier = null;
    },
    fetchSupplierOptions() {
      // 如果父組件已經傳遞了 supplierOptions，則不需要重新獲取
      if (this.supplierOptions && this.supplierOptions.length > 0) {
        return;
      }

      if (this.showSupplierSelect) {
        // 使用 fetchOptionList 獲取所有供應商（包括未啟用的）
        this.$store
          .dispatch("app/fetchOptionList", {
            suppliers: true,
            suppliers_all: true, // 包含所有供應商（包括未啟用的）
          })
          .then((response) => {
            // 優先使用 suppliers_all，如果不存在則使用 suppliers
            this.localSupplierOptions = response.data.suppliers_all || response.data.suppliers || [];
            console.log("Modal 獲取到的供應商選項:", this.localSupplierOptions);
          })
          .catch((error) => {
            console.error("獲取供應商列表失敗:", error);
            // 如果 suppliers_all 不存在，嘗試使用原來的方法
            this.$store
              .dispatch("supplier/fetchSuppliers", {
                perPage: 1000, // 獲取所有供應商
                page: 1,
                is_active: null, // 不過濾 is_active 狀態
              })
              .then((response) => {
                this.localSupplierOptions = response.data.entities || [];
              })
              .catch((fallbackError) => {
                console.error("備用方法獲取供應商列表也失敗:", fallbackError);
              });
          });
      }
    },
    onFileUploaded(file) {
      this.newDocumentData.file = file;
    },
    invoiceSearch(options, search) {
      return options.filter((item) => {
        return item.invoice_number.toLowerCase().includes(search.toLowerCase())
          || item.name_cn.toLowerCase().includes(search.toLowerCase())
          || item.address.toLowerCase().includes(search.toLowerCase());
      });
    },
    onInvoiceSelect(invoiceId, itemIndex) {
      if (invoiceId) {
        // 找到選中的發票
        const selectedInvoice = this.invoiceOptions.find(invoice => invoice.id === invoiceId);
        if (selectedInvoice) {
          // 計算未付金額
          const totalAmount = Number(selectedInvoice.total_amount) || 0;
          const totalPaid = Number(selectedInvoice.total_paid) || 0;
          const unpaidAmount = totalAmount - totalPaid;

          // 自動填入未付金額到對應的項目
          if (unpaidAmount > 0) {
            this.newDocumentData.items[itemIndex].amount = unpaidAmount.toFixed(2);
          } else {
            // 如果已付清，設為 0
            this.newDocumentData.items[itemIndex].amount = 0;
          }
        }
      }
    },
    addItem(index) {
      this.newDocumentData.items.splice(index + 1, 0,
        JSON.parse(
          JSON.stringify({
            id: 0,
            invoice_id: null,
            amount: 0,
            title: "",
            daily_expense_date: null,
          })
        )
      );
    },
    removeItem(index, id) {
      // check number of record
      let record =
        this.newDocumentData &&
        this.newDocumentData.items &&
        this.newDocumentData.items.length;
      if (record <= 1) {
        this.makeToast("danger", "Warning", "請至少留有一個表單。");
      } else {
        if (id > 0) {
          this.$swal({
            title: "你確定嗎?",
            text: "您的行動是最終的，您將無法檢索這記錄。",
            showCancelButton: true,
            confirmButtonText: "刪除",
            cancelButtonText: "取消",
            customClass: {
              confirmButton: "btn btn-primary",
              cancelButton: "btn btn-outline-danger ml-1",
            },
            buttonsStyling: false,
          }).then((result) => {
            if (result.value) {
              store
                .dispatch("supplier/deleteSupplierCostSummary", { id })
                .then((response) => {
                  this.$swal({
                    text: response.data.message,
                    icon: "success",
                    confirmButtonText: "確定",
                    customClass: {
                      confirmButton: "btn btn-primary",
                    },
                    showClass: {
                      popup: "animate__animated animate__bounceIn",
                    },
                    buttonsStyling: false,
                  });
                  this.$emit("refetch-data");
                })
                .catch((error) => {
                  this.$swal({
                    text: error.response.data.message,
                    icon: "error",
                    confirmButtonText: "確定",
                    customClass: {
                      confirmButton: "btn btn-primary",
                    },
                    showClass: {
                      popup: "animate__animated animate__bounceIn",
                    },
                    buttonsStyling: false,
                  });
                });
            }
          });
        } else {
          this.newDocumentData.items.splice(index, 1);
        }
      }
    },
    onSubmit() {
      // 驗證供應商選擇
      if (this.showSupplierSelect && !this.selectedSupplier) {
        this.$swal({
          text: "請選擇供應商",
          icon: "warning",
          confirmButtonText: "確定",
          customClass: {
            confirmButton: "btn btn-primary",
          },
          buttonsStyling: false,
        });
        return;
      }

      // 檢查是否需要顯示付款憑證同步警告
      const confirmTitle = this.showPaymentProofWarning
        ? "確認修改帳單金額"
        : "你確定嗎?";

      const confirmText = this.showPaymentProofWarning
        ? "此帳單已有付款憑證，修改帳單金額將同步更新付款憑證金額。是否繼續？"
        : "";

      this.$swal({
        title: confirmTitle,
        text: confirmText,
        icon: this.showPaymentProofWarning ? "warning" : undefined,
        showCancelButton: true,
        confirmButtonText: "提交",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          let formData = new FormData();

          formData.append("id", this.newDocumentData.id)
          formData.append("user_id", this.showSupplierSelect ? this.selectedSupplier : this.user_id)
          formData.append("document_type", 1) // 帳單類型
          formData.append("is_daily_expense", this.newDocumentData.is_daily_expense == 1 || this.newDocumentData.is_daily_expense === true ? 1 : 0)
          formData.append("bill_number", this.newDocumentData.bill_number || '')
          formData.append("bill_date", this.newDocumentData.bill_date || '')
          formData.append("bill_total_amount", this.newDocumentData.bill_total_amount || 0)
          for (let i = 0; i < this.newDocumentData.items.length; i++) {
            formData.append(`items[${i}][title]`, this.newDocumentData.items[i].title);
            // 確保 invoice_id 為 null 或 0 時提交空字串（後端會轉為 null）
            formData.append(`items[${i}][invoice_id]`, this.newDocumentData.items[i].invoice_id || '');
            formData.append(`items[${i}][amount]`, this.newDocumentData.items[i].amount);
            formData.append(`items[${i}][daily_expense_date]`, this.newDocumentData.items[i].daily_expense_date || '');
          }
          if (
            this.newDocumentData.file
          ) {
            formData.append("file", this.newDocumentData.file);
          }


          this.loading = true;
          axiosIns
            .post(this.apiUrl, formData)
            .then((response) => {
              this.loading = false;
              this.$swal({
                text: response.data.message,
                icon: "success",
                confirmButtonText: "確定",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.$emit("refetch-data");
              this.$bvModal.hide("modal-upload-bill");
            })
            .catch((error) => {
              this.loading = false;
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                confirmButtonText: "確定",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },
  },
  watch: {
    showSupplierSelect: {
      handler(newVal) {
        if (newVal) {
          this.fetchSupplierOptions();
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.fetchSupplierOptions();
  },
  setup(props) {
    const loading = ref(false);
    const newDocumentData = ref({
      id: 0,
      items: [{ id: 0, invoice_id: null, amount: 0, title: "", daily_expense_date: null }],
      is_daily_expense: 0,
      bill_number: '',
      bill_date: null,
      bill_total_amount: null
    });
    const selectedSupplier = ref(null);
    const isLoadingData = ref(false); // 標記是否正在載入資料

    watch(
      () => props.documentData,
      (newVal) => {
        // 設定載入標記，避免觸發開支類型切換的清空邏輯
        isLoadingData.value = true;
        // 使用深拷貝確保 items 陣列中的每個項目都被正確複製
        newDocumentData.value = JSON.parse(JSON.stringify(newVal));
        // 延遲重置載入標記，確保所有 watch 都執行完畢
        setTimeout(() => {
          isLoadingData.value = false;
        }, 100);
      }
    );

    // 監聽開支類型切換，自動清空所有項目的兩個欄位
    watch(
      () => newDocumentData.value.is_daily_expense,
      (_newValue, oldValue) => {
        // 只有在真正切換時才清空（避免初始化時觸發和載入資料時觸發）
        if (!isLoadingData.value && oldValue !== undefined && newDocumentData.value.items && newDocumentData.value.items.length > 0) {
          newDocumentData.value.items.forEach(item => {
            // 每次切換都清空兩個欄位，讓用戶重新選擇
            item.invoice_id = null;
            item.daily_expense_date = null;
          });
        }
      }
    );

    const resetDocumentData = () => {
      props.documentData = JSON.parse(JSON.stringify({ id: 0 }));
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetDocumentData);

    return {
      loading,
      newDocumentData,
      selectedSupplier,
      refFormObserver,
      getValidationState,
      resetForm,
    };
  },
};
</script>
  
  <style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
  